import { useState, useMemo, useCallback } from "react";
import { Badge, Button, Flex, Section, Text } from "@radix-ui/themes";
import { PlusIcon, Pencil1Icon, TrashIcon } from "@radix-ui/react-icons";
import { useQualitySearch } from "../QualitySearchContext";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import {
  useSamplingApi,
  SampleForm,
} from "@/v2/entities/sampling/Sampling.api";
import { useQuery } from "react-query";
import { TargetRuleBuilder } from "@/v2/features/sampling/TargetStep";
import { DeleteSampleFormDialog } from "@/v2/features/sampling/DeleteSampleFormDialog";
import { ColumnDef } from "@tanstack/react-table";
import { DateFormatter } from "@/v2/components/formatters/DateFormatter";
import { RowActions, SimpleTable } from "@/v2/components/table/SimpleTable";
import { TextWithTooltip } from "@/components/shared/tooltip";

const today = dayjs().startOf("day");
const yesterday = dayjs().subtract(1, "day").startOf("day");

const columns: ColumnDef<SampleForm>[] = [
  {
    header: "Name",
    accessorKey: "name",
    cell: ({ row }) => {
      return (
        <TextWithTooltip weight="medium" size="2">
          {row.original.name}
        </TextWithTooltip>
      );
    },
    meta: {
      className: "w-[200px]",
    },
  },
  {
    header: "Targeted Interactions",
    accessorKey: "qaRules",
    cell: ({ row }) => {
      const qaRules = row.original.qaRules;

      if (qaRules == null) {
        return;
      }

      return (
        <TargetRuleBuilder
          value={qaRules}
          onChange={() => {}}
          viewOnly
          size="1"
        />
      );
    },
  },
  {
    header: "Validation Sets",
    accessorKey: "sampleConfiguration",
    cell: ({ row }) => {
      const enabledSampleConfigurations =
        row.original.sampleConfiguration?.filter((c) => c.isEnabled) ?? [];

      if (enabledSampleConfigurations.length === 0) {
        return;
      }

      return (
        <Badge color="green" highContrast>
          {enabledSampleConfigurations.length}
        </Badge>
      );
    },
    meta: {
      className: "w-[160px]",
    },
  },
  {
    header: "Questions",
    accessorKey: "questions",
    cell: ({ row }) => {
      const questions = row.original.questions;
      if (questions == null) {
        return;
      }

      return <Badge>{questions.length}</Badge>;
    },
    meta: {
      className: "w-[100px]",
    },
  },
  {
    header: "Created",
    accessorKey: "createdAt",
    cell: ({ row }) => {
      const createdAt = row.original.createdAt;
      if (createdAt == null) {
        return;
      }

      if (dayjs(createdAt).isSame(today, "day")) {
        return "Today";
      } else if (dayjs(createdAt).isSame(yesterday, "day")) {
        return "Yesterday";
      }

      return <DateFormatter value={createdAt} />;
    },
    meta: {
      className: "w-[160px]",
    },
  },
];

interface ScorecardsListPageProps {
  isV2?: boolean;
}

export function ScorecardsListPage({ isV2 = false }: ScorecardsListPageProps) {
  const navigate = useNavigate();
  const { getSampleForms } = useSamplingApi();
  const { searchPhrase } = useQualitySearch();

  const { data, refetch } = useQuery({
    queryKey: ["sample-forms"],
    queryFn: getSampleForms,
  });

  const [formToDelete, setFormToDelete] = useState<number | undefined>();

  const filtered = useMemo(() => {
    if (!data) return [];
    return data
      ?.filter(
        (form) =>
          (form.name &&
            form.name.toLowerCase().includes(searchPhrase.toLowerCase())) ||
          form.questions?.some(
            (q) =>
              q.content &&
              q.content.toLowerCase().includes(searchPhrase.toLowerCase())
          )
      )
      ?.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
  }, [data, searchPhrase]);

  const handleRowClicked = useCallback(
    (form: SampleForm) => {
      navigate(`/quality/scorecards/${form.id}`);
    },
    [navigate]
  );

  const onEditClick = useCallback(
    (form: SampleForm) => {
      navigate(`/quality/scorecards/${form.id}/edit`);
    },
    [navigate]
  );

  const onDeleteClick = useCallback(
    (form: SampleForm) => {
      setFormToDelete(form.id);
    },
    [setFormToDelete]
  );

  const onDeleteSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  const rowActions = useMemo<RowActions<SampleForm>>(() => {
    return [
      {
        label: "Edit",
        icon: <Pencil1Icon />,
        onClick: onEditClick,
      },
      {
        label: "Delete",
        icon: <TrashIcon />,
        onClick: onDeleteClick,
      },
    ];
  }, [onEditClick, onDeleteClick]);

  return (
    <>
      <Section size="4" pt="0">
        <Flex p="6" direction="column" gap="3" width="100%">
          <Flex direction={"row"} gap={"2"} align="center">
            <Flex gap={"2"} align={"center"}>
              <Text size={"3"} weight={"medium"}>
                Scorecards
              </Text>
              <Badge color="gray">{data?.length ?? 0}</Badge>
            </Flex>
          </Flex>
          <SimpleTable
            data={filtered}
            columns={columns}
            rowActions={rowActions}
            onRowClicked={handleRowClicked}
          />
        </Flex>
      </Section>

      <DeleteSampleFormDialog
        open={formToDelete != null}
        onClose={() => setFormToDelete(undefined)}
        formId={formToDelete}
        onSuccess={onDeleteSuccess}
      />
    </>
  );
}

export default ScorecardsListPage;
